import { api } from "@/redux/api";

/** The authorization request an MCP client sent, passed through from the URL unchanged. */
export type McpAuthorizeParams = Record<string, string>;

export interface McpAuthorizeDetails {
	clientName: string;
	redirectHost: string;
	/** Only a Global Admin can connect an MCP client (BCM360-383). */
	allowed: boolean;
}

export interface McpConnection {
	id: string;
	clientName: string;
	createdAt: string;
	lastUsedAt: string | null;
}

/**
 * The MCP server's OAuth consent and the user's connected AI clients — BCM360-383. The API
 * checks the request; this app logs the user in (with 2FA) and asks for consent.
 */
export const mcpApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getMcpAuthorizeDetails: builder.query<McpAuthorizeDetails, McpAuthorizeParams>({
			query: (params) => ({ url: `mcp/oauth/authorize/details?${new URLSearchParams(params)}` }),
		}),
		approveMcpAuthorize: builder.mutation<{ redirectTo: string }, McpAuthorizeParams>({
			query: (body) => ({ url: "mcp/oauth/authorize/approve", method: "POST", body }),
			invalidatesTags: ["McpConnections"],
		}),
		denyMcpAuthorize: builder.mutation<{ redirectTo: string }, McpAuthorizeParams>({
			query: (body) => ({ url: "mcp/oauth/authorize/deny", method: "POST", body }),
		}),
		getMcpConnections: builder.query<McpConnection[], void>({
			query: () => ({ url: "mcp/oauth/connections" }),
			providesTags: ["McpConnections"],
		}),
		deleteMcpConnection: builder.mutation<void, string>({
			query: (id) => ({ url: `mcp/oauth/connections/${id}`, method: "DELETE" }),
			invalidatesTags: ["McpConnections"],
		}),
	}),
});

export const {
	useGetMcpAuthorizeDetailsQuery,
	useApproveMcpAuthorizeMutation,
	useDenyMcpAuthorizeMutation,
	useGetMcpConnectionsQuery,
	useDeleteMcpConnectionMutation,
} = mcpApi;
